import { agentErrorForFeedback } from "../../../lib/aiAgentSecrets";
import React, { useState } from "react";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { handleApiSuccess, handleApiError } from "@finitless/shared/lib";
import { useAiAgents, useUpdateAiAgent } from "../../../hooks/useAiAgents";
import AgentMonitoringSection from "./AgentMonitoringSection";
import AgentDetailModal from "./AgentDetailModal";
import CreateAgentModal from "./CreateAgentModal";
import ProviderSchemasInfo from "./ProviderSchemasInfo";
import WhatsAppTestModal from "./WhatsAppTestModal";
import BusinessLoadingState from "../../ui/States/BusinessLoadingState";
import { WidgetConfigModal } from "../../ui/Widgets/AiChatWidget";
import { Button } from "../../ui/UIElements/Button";
import { Icon } from "../../ui/UIElements/Icon";
import "./ai-agents-ordering.css";

const AiAgentsPage = ({ selectedBrand }) => {
  const { t } = useTranslation("aiAgents");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [widgetAgent, setWidgetAgent] = useState(null);
  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);
  const [whatsAppTestAgent, setWhatsAppTestAgent] = useState(null);
  const [isWhatsAppTestModalOpen, setIsWhatsAppTestModalOpen] = useState(false);
  const [togglingAgentId, setTogglingAgentId] = useState(null);
  const [autoConnectOAuth, setAutoConnectOAuth] = useState(false);

  const subtitle = t("page.subtitle");

  const toast = useToast();

  // Fetch AI agents data
  const {
    data: agentsData,
    isLoading,
    error,
  } = useAiAgents({
    enabledFilter: "all",
    pageSize: 50, // Get more agents for display
  });

  const updateAiAgentMutation = useUpdateAiAgent();

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
    setAutoConnectOAuth(false);
  };

  const handleCreateSuccess = () => {
    // Refresh agents data
    // The query will automatically refetch due to invalidation in the mutation
  };

  // Handle agent created for OAuth - close create modal, open detail modal with auto-connect
  const handleCreatedForOAuth = (agent) => {
    setIsCreateModalOpen(false);
    setSelectedAgent(agent);
    setAutoConnectOAuth(true);
    setIsModalOpen(true);
  };

  const handleWidgetClick = (agent) => {
    setWidgetAgent(agent);
    setIsWidgetModalOpen(true);
  };

  const handleCloseWidgetModal = () => {
    setIsWidgetModalOpen(false);
    setWidgetAgent(null);
  };

  const handleTestWhatsApp = (agent) => {
    setWhatsAppTestAgent(agent);
    setIsWhatsAppTestModalOpen(true);
  };

  const handleCloseWhatsAppTestModal = () => {
    setIsWhatsAppTestModalOpen(false);
    setWhatsAppTestAgent(null);
  };

  const handleToggleEnabled = async (agent, nextEnabled) => {
    if (!agent?.id) return;
    if (updateAiAgentMutation.isPending) return;

    setTogglingAgentId(agent.id);
    try {
      await updateAiAgentMutation.mutateAsync({
        agentId: agent.id,
        enabled: nextEnabled,
      });
      handleApiSuccess(
        t("toasts.toggleSuccess", {
          status: nextEnabled ? t("status.enabled") : t("status.disabled"),
        }),
        toast,
      );
    } catch (error) {
      handleApiError(agentErrorForFeedback(error), toast, {
        title: t("detail.toasts.updateErrorTitle"),
        defaultMessage: t("detail.toasts.updateErrorMessage"),
      });
    } finally {
      setTogglingAgentId((prev) => (prev === agent.id ? null : prev));
    }
  };

  // Get the updated agent from the query when available
  const currentAgent =
    selectedAgent && agentsData?.agents
      ? agentsData.agents.find((a) => a.id === selectedAgent.id) ||
        selectedAgent
      : selectedAgent;
  const currentWhatsAppTestAgent =
    whatsAppTestAgent && agentsData?.agents
      ? agentsData.agents.find((a) => a.id === whatsAppTestAgent.id) ||
        whatsAppTestAgent
      : whatsAppTestAgent;

  if (isLoading) {
    return <BusinessLoadingState />;
  }

  if (error) {
    return (
      <div className="ordering-ai-agents-page">
        <PageHeader
          title={t("page.title")}
          subtitle={subtitle}
          selectedBrand={selectedBrand}
        />
        <div className="border-destructive/50 rounded-lg border bg-[var(--error-bg)] p-4 text-center">
          <p className="text-destructive">{t("page.loadError")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ordering-ai-agents-page">
      <PageHeader
        title={t("page.title")}
        subtitle={subtitle}
        selectedBrand={selectedBrand}
      />

      <div className="ordering-ai-agents-content space-y-8">
        {/* AI Agents Monitoring Section */}
        <div className="flex items-center justify-between">
          <h2 className="text-foreground text-2xl font-bold">
            {t("page.sections.monitoringTitle")}
          </h2>
          <Button
            type="button"
            intent="brand"
            emphasis="solid"
            size="touch"
            onClick={() => setIsCreateModalOpen(true)}
            className="ordering-ai-agents-primary-action"
          >
            <Icon name="Plus" className="h-5 w-5" />
            <span>{t("page.actions.createAgent")}</span>
          </Button>
        </div>

        <AgentMonitoringSection
          agents={agentsData?.agents || []}
          onAgentClick={handleAgentClick}
          onWidgetClick={handleWidgetClick}
          onTestWhatsApp={handleTestWhatsApp}
          onToggleEnabled={handleToggleEnabled}
          togglingAgentId={togglingAgentId}
        />

        {/* Provider Schemas Information */}
        <ProviderSchemasInfo />
      </div>

      {/* Agent Detail Modal */}
      {isModalOpen && currentAgent && (
        <AgentDetailModal
          agent={currentAgent}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          autoConnectOAuth={autoConnectOAuth}
        />
      )}

      {/* Create Agent Modal */}
      <CreateAgentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        onCreatedForOAuth={handleCreatedForOAuth}
      />

      {/* Widget Config Modal */}
      <WidgetConfigModal
        isOpen={isWidgetModalOpen}
        onClose={handleCloseWidgetModal}
        agent={widgetAgent}
      />

      <WhatsAppTestModal
        isOpen={isWhatsAppTestModalOpen}
        onClose={handleCloseWhatsAppTestModal}
        agent={currentWhatsAppTestAgent}
      />
    </div>
  );
};

export default AiAgentsPage;
